// ══════════════════════════════════════════════════════════════════════════════
//  ZoomPellizco: acercar una HOJA con dos dedos SIN mover la página.
// ══════════════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from 'react';
import { C, E, M, R, T } from '../../design.js';

// ══════════════════════════════════════════════
//  El viewport de la app declara maximum-scale=1 (index.html, contra el zoom
//  automático de iOS al enfocar un campo), así que el pellizco del navegador
//  está apagado en toda la app. Un documento en pantalla sí necesita acercarse:
//  esta caja lo resuelve DENTRO de sí misma, sin tocar el resto de la vista.
//    · dos dedos acercan y alejan, anclados al punto que se pellizca
//    · con la hoja acercada, un dedo la arrastra (y el scroll de la página
//      vuelve solo en cuanto se ajusta de nuevo)
//    · doble toque alterna entre ajustado y 2x
//  Sin táctil (escritorio) es una caja normal: no estorba ni cambia nada.
// ══════════════════════════════════════════════
const acotar = (v, min, max) => Math.min(Math.max(v, min), max);
const DOBLE_MS = 300;   // ventana del doble toque
const DOBLE_PX = 30;    // cuánto se puede mover el dedo y seguir siendo toque

// ══════════════════════════════════════════════
//  usePellizcoEscala — el mismo gesto, pero para hojas que CRECEN de verdad.
//  La hoja del presupuesto escala su tipografia (todo el documento esta en em)
//  y su contenedor la desplaza, asi que se lee nitida a cualquier tamano: aqui
//  no se transforma nada, el pellizco solo mueve el mismo zoom que los botones.
//    usePellizcoEscala(ref, { valor: zoom, onValor: fijarZoom, min, max })
// ══════════════════════════════════════════════
const usePellizcoEscala = (ref, { valor = 1, onValor = null, min = 0.6, max = 2.5, dobleToque = true } = {}) => {
  const vivo  = useRef({ valor, onValor, min, max });
  vivo.current = { valor, onValor, min, max };
  const gesto = useRef(null);
  const toque = useRef({ t: 0, x: 0, y: 0 });

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const dist = (t) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY) || 1;

    const onStart = (e) => {
      if (e.touches.length === 2) {
        gesto.current = { d0: dist([e.touches[0], e.touches[1]]), v0: vivo.current.valor };
        e.preventDefault();
      } else if (e.touches.length === 1 && dobleToque) {
        const t = e.touches[0];
        const ahora = Date.now();
        const prev = toque.current;
        if (ahora - prev.t < DOBLE_MS && Math.hypot(t.clientX - prev.x, t.clientY - prev.y) < DOBLE_PX) {
          const { valor: v, onValor: cb, max: mx } = vivo.current;
          cb?.(v > 1 ? 1 : Math.min(2, mx));
          toque.current = { t: 0, x: 0, y: 0 };
          e.preventDefault();
          return;
        }
        toque.current = { t: ahora, x: t.clientX, y: t.clientY };
      }
    };
    const onMove = (e) => {
      const g = gesto.current;
      if (!g || e.touches.length !== 2) return;
      const { min: mn, max: mx, onValor: cb } = vivo.current;
      const v = acotar(g.v0 * (dist([e.touches[0], e.touches[1]]) / g.d0), mn, mx);
      cb?.(Math.round(v * 100) / 100);
      e.preventDefault();
    };
    const onEnd = (e) => { if (e.touches.length < 2) gesto.current = null; };
    // Safari tiene su propio pellizco de pagina: sobre la hoja manda el nuestro.
    const noSafari = (e) => e.preventDefault();

    el.addEventListener('touchstart',    onStart,  { passive: false });
    el.addEventListener('touchmove',     onMove,   { passive: false });
    el.addEventListener('touchend',      onEnd);
    el.addEventListener('touchcancel',   onEnd);
    el.addEventListener('gesturestart',  noSafari, { passive: false });
    el.addEventListener('gesturechange', noSafari, { passive: false });
    return () => {
      el.removeEventListener('touchstart',    onStart);
      el.removeEventListener('touchmove',     onMove);
      el.removeEventListener('touchend',      onEnd);
      el.removeEventListener('touchcancel',   onEnd);
      el.removeEventListener('gesturestart',  noSafari);
      el.removeEventListener('gesturechange', noSafari);
    };
  }, [ref, dobleToque]);
};

const ZoomPellizco = ({ children, max = 4, indicador = true, style: sx = {}, className = '' }) => {
  const cajaRef   = useRef(null);
  const lienzoRef = useRef(null);
  const vivo      = useRef({ k: 1, x: 0, y: 0 });   // transform en curso, sin re-render por dedo
  const gesto     = useRef(null);
  const toque     = useRef({ t: 0, x: 0, y: 0 });
  const [k, setK] = useState(1);                    // solo para el chrome y el touch-action

  const pintar = useCallback((suave = false) => {
    const el = lienzoRef.current;
    if (!el) return;
    const { k: kk, x, y } = vivo.current;
    // El pellizco es manipulacion directa: mientras el dedo manda no hay
    // transicion. La curva es solo para el doble toque y el ajuste.
    el.style.transition = suave ? `transform ${M.fast} ${M.ease}` : 'none';
    el.style.transform = kk === 1 ? 'none' : `translate(${x}px, ${y}px) scale(${kk})`;
  }, []);

  // La hoja no se despega de su caja: con k=1 vuelve a su sitio.
  const acomodar = useCallback(() => {
    const caja = cajaRef.current;
    if (!caja) return;
    const { k: kk } = vivo.current;
    vivo.current.x = acotar(vivo.current.x, Math.min(0, caja.clientWidth  * (1 - kk)), 0);
    vivo.current.y = acotar(vivo.current.y, Math.min(0, caja.clientHeight * (1 - kk)), 0);
  }, []);

  const ajustar = useCallback((kk = 1, centro = null) => {
    const caja = cajaRef.current;
    if (kk === 1 || !caja) { vivo.current = { k: 1, x: 0, y: 0 }; }
    else {
      const c = centro || { x: caja.clientWidth / 2, y: caja.clientHeight / 2 };
      const p = { x: (c.x - vivo.current.x) / vivo.current.k, y: (c.y - vivo.current.y) / vivo.current.k };
      vivo.current = { k: kk, x: c.x - p.x * kk, y: c.y - p.y * kk };
      acomodar();
    }
    pintar(true);
    setK(vivo.current.k);
  }, [acomodar, pintar]);

  useEffect(() => {
    const caja = cajaRef.current;
    if (!caja) return undefined;
    const dist = t => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY) || 1;
    const medio = t => {
      const r = caja.getBoundingClientRect();
      return { x: (t[0].clientX + t[1].clientX) / 2 - r.left, y: (t[0].clientY + t[1].clientY) / 2 - r.top };
    };
    const arrastre = (t) => ({ tipo: 'arrastre', x0: t.clientX, y0: t.clientY, ox: vivo.current.x, oy: vivo.current.y });

    const onStart = (e) => {
      if (e.touches.length === 2) {
        const t = [e.touches[0], e.touches[1]];
        const c = medio(t);
        const { k: kk, x, y } = vivo.current;
        gesto.current = { tipo: 'pellizco', d0: dist(t), k0: kk, punto: { x: (c.x - x) / kk, y: (c.y - y) / kk } };
        e.preventDefault();
      } else if (e.touches.length === 1) {
        const t = e.touches[0];
        const ahora = Date.now();
        const prev = toque.current;
        if (ahora - prev.t < DOBLE_MS && Math.hypot(t.clientX - prev.x, t.clientY - prev.y) < DOBLE_PX) {
          const r = caja.getBoundingClientRect();
          ajustar(vivo.current.k > 1 ? 1 : Math.min(2, max), { x: t.clientX - r.left, y: t.clientY - r.top });
          toque.current = { t: 0, x: 0, y: 0 };
          gesto.current = null;
          e.preventDefault();
          return;
        }
        toque.current = { t: ahora, x: t.clientX, y: t.clientY };
        if (vivo.current.k > 1) gesto.current = arrastre(t);
      }
    };

    const onMove = (e) => {
      const g = gesto.current;
      if (!g) return;
      if (g.tipo === 'pellizco' && e.touches.length === 2) {
        const t = [e.touches[0], e.touches[1]];
        const kk = acotar(g.k0 * (dist(t) / g.d0), 1, max);
        const c = medio(t);
        vivo.current = { k: kk, x: c.x - g.punto.x * kk, y: c.y - g.punto.y * kk };
        acomodar();
        pintar();
        e.preventDefault();
      } else if (g.tipo === 'arrastre' && e.touches.length === 1) {
        const t = e.touches[0];
        vivo.current.x = g.ox + (t.clientX - g.x0);
        vivo.current.y = g.oy + (t.clientY - g.y0);
        acomodar();
        pintar();
        e.preventDefault();
      }
    };

    const onEnd = (e) => {
      if (e.touches.length === 1 && gesto.current?.tipo === 'pellizco') gesto.current = arrastre(e.touches[0]);
      else if (e.touches.length === 0) gesto.current = null;
      setK(vivo.current.k);
    };

    caja.addEventListener('touchstart',  onStart, { passive: false });
    caja.addEventListener('touchmove',   onMove,  { passive: false });
    caja.addEventListener('touchend',    onEnd);
    caja.addEventListener('touchcancel', onEnd);
    return () => {
      caja.removeEventListener('touchstart',  onStart);
      caja.removeEventListener('touchmove',   onMove);
      caja.removeEventListener('touchend',    onEnd);
      caja.removeEventListener('touchcancel', onEnd);
    };
  }, [acomodar, ajustar, max, pintar]);

  return (
    <div
      ref={cajaRef}
      // `gb-sin-gesto`: esta superficie ya tiene su propio doble toque (300 ms),
      // así que el gesto global del buscador no cuenta los toques de aquí. Sin
      // la marca, tres toques sobre una hoja la acercarían Y abrirían el panel.
      className={['gb-sin-gesto', className].filter(Boolean).join(' ')}
      style={{
        position: 'relative', overflow: 'hidden',
        // Ajustado, el dedo sigue haciendo scroll de la página; acercado, el
        // dedo arrastra la hoja.
        touchAction: k > 1 ? 'none' : 'pan-y',
        ...sx,
      }}
    >
      {indicador && k > 1 && (
        <div style={{ position: 'sticky', top: 8, zIndex: 3, height: 0, display: 'flex', justifyContent: 'flex-end', pointerEvents: 'none' }}>
          <button
            type="button" className="gb-press gb-i gb-focus" onClick={() => ajustar(1)}
            title="Ajustar la hoja al ancho" aria-label="Ajustar la hoja al ancho"
            style={{
              pointerEvents: 'auto', marginRight: 8, display: 'inline-flex', alignItems: 'center', gap: 6,
              minHeight: 32, padding: '0 10px', borderRadius: R.pill, cursor: 'pointer', fontFamily: 'inherit',
              border: `1px solid ${C.border}`, background: C.flotante, boxShadow: E.e2,
              ...T.micro, fontWeight: 600, color: C.textMed,
            }}
          >
            <span className="num-tabular">{Math.round(k * 100)}%</span>
            Ajustar
          </button>
        </div>
      )}
      <div ref={lienzoRef} style={{ transformOrigin: '0 0' }}>
        {children}
      </div>
    </div>
  );
};

export { ZoomPellizco, usePellizcoEscala };
